import React from "react";
import { CustomStatCard } from "subframe/components/CustomStatCard";
import { ExitSurveyFilter } from "modules/Retention/types/exitSurvey";
import { useQuery } from "@tanstack/react-query";
import { fetchExitSurveySummary } from "modules/Retention/services/retention";
import endpoints from "modules/Retention/constants/endpoints";
import { getDateRangeFromTimeRange } from "utils/helper";

interface ExitSurveyStatsProps {
  showAllStats?: boolean;
  status: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  filters?: ExitSurveyFilter;
  onFilterChange?: (filters: ExitSurveyFilter) => void;
  loading?: boolean;
}

const ExitSurveyStatsSkeleton: React.FC<{ showAllStats?: boolean }> = ({ showAllStats = true }) => {
  if (showAllStats) {
    return (
      <div className="flex w-full flex-wrap items-start gap-2">
        <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
        <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
        <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
        <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
        <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
      </div>
    );
  } else {
    return (
      <div className="flex w-full flex-wrap items-start gap-2">
        <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
        <div className="h-20 md:h-24 w-28 md:w-36 flex-none bg-gray-200 rounded-md animate-pulse"></div>
      </div>
    );
  }
};

export const ExitSurveyStats: React.FC<ExitSurveyStatsProps> = ({
  showAllStats = true,
  status,
  setStatus,
  filters,
  onFilterChange,
}) => {
  // Prepare API parameters for summary (excluding pagination and status)
  const summaryParams: any = {};

  // Only include location if it's not "all"
  if (filters?.branch && filters.branch !== "all") {
    summaryParams.location = filters.branch;
  }

  // Only include job if it's not "all"
  if (filters?.job && filters.job !== "all") {
    summaryParams.job = filters.job;
  }

  // Include date range if available
  if (filters?.timeRange) {
    const dateRange = getDateRangeFromTimeRange(filters.timeRange);
    Object.assign(summaryParams, dateRange);
  }

  const { data: displayStats, isLoading } = useQuery({
    queryKey: [endpoints.exitSurveySummary, filters?.timeRange, filters?.branch, filters?.job],
    queryFn: () => fetchExitSurveySummary(summaryParams),
    select: (res:any) => res?.result?.exit_surveys,
  });

  const handleCardClick = (statusValue: string) => {
    setStatus(statusValue === status ? '' : statusValue);
    // Reset all filters when status changes
    if (onFilterChange && filters) {
      onFilterChange({
        ...filters,
        timeRange: 'all', // Reset to "Past 2 weeks"
        branch: 'all',   // Reset to "All locations"
        job: 'all',      // Reset to "All jobs"
        rankBy: 'exit_date' // Reset to default
      });
    }
  };

  const getCardWrapperClass = (statusValue: string, colorClass: string) => {
    return status === statusValue ? `border border-solid ${colorClass} rounded-md` : '';
  };

  if (isLoading || !displayStats) {
    return <ExitSurveyStatsSkeleton showAllStats={showAllStats} />;
  }

  // Show all cards for Exit Survey Analysis
  return (
    <div className="flex w-full flex-wrap items-start gap-2">
      <div
        className="w-40 md:w-44 flex-none transition-all duration-200"
        onClick={() => handleCardClick('')}
      >
        <CustomStatCard
          title="Total surveys"
          variant="total"
          value={displayStats.total_surveys}
        />
      </div>
      <div
        className={`w-40 md:w-44 flex-none transition-all duration-200 ${getCardWrapperClass('completed', 'border-success-600')}`}
        onClick={() => handleCardClick('completed')}
      >
        <CustomStatCard
          title="Completed"
          value={displayStats.completed}
          variant="resolved"
        />
      </div>

      <div
        className={`w-40 md:w-44 flex-none transition-all duration-200 ${getCardWrapperClass('pending-hrbp', 'border-error-600')}`}
        onClick={() => handleCardClick('pending-hrbp')}
      >
        <CustomStatCard
          title="Pending"
          subtitle="HRBP"
          value={displayStats.pending_hrbp}
          variant="at-risk"
        />
      </div>

      <div
        className={`w-40 md:w-44 flex-none transition-all duration-200 ${getCardWrapperClass('pending-both', 'border-warning-600')}`}
        onClick={() => handleCardClick('pending-both')}
      >   
        <CustomStatCard
          title="Pending"
          subtitle="Both"
          value={displayStats.pending_both}
          variant="escalated"
        />
      </div>

      <div
        className={`w-40 md:w-44 flex-none transition-all duration-200 ${getCardWrapperClass('pending-employee', 'border-neutral-600')}`}
        onClick={() => handleCardClick('pending-employee')}
      >
        <CustomStatCard
          title="Pending"
          subtitle="Employee"
          value={displayStats.pending_employee}
          variant="pending"
        />
      </div>
    </div>
  );
}; 